import { ApiError } from "@/lib/apiClient";

/** Backend error codes for per-user assignment limits (1 Focus, 2 Queue). */
export const FOCUS_LIMIT_EXCEEDED = "FOCUS_LIMIT_EXCEEDED";
export const QUEUE_LIMIT_EXCEEDED = "QUEUE_LIMIT_EXCEEDED";

/** Backend error code: current Focus task has no status update posted this week. */
export const FOCUS_UPDATE_REQUIRED = "FOCUS_UPDATE_REQUIRED";

/**
 * Backend error code: one save both closed/parked the priority and put someone into Focus or Queue on
 * it. The backend refuses the pair rather than picking a winner, because honouring the row would leave
 * it holding that person's Focus slot on a task nobody is working on.
 */
export const ACTIVE_ASSIGNMENT_ON_INACTIVE_PRIORITY = "ACTIVE_ASSIGNMENT_ON_INACTIVE_PRIORITY";

/**
 * Backend error code: a task-level change (details, status, assignees, DRI) was attempted by someone
 * who is not the DRI, the creator or an admin. `details` carries the DRI's name.
 *
 * Distinct from `PRIORITY_NOT_AUTHORIZED`, which means cross-tenant access — one message would be
 * wrong for both.
 */
export const PRIORITY_DRI_ONLY = "PRIORITY_DRI_ONLY";

/**
 * Backend error code: an assignee tried to edit, swap into focus or re-assign a candidate (a
 * deliverable with no DRI). The BE wants an owner first — `POST /priorities/:pid/assignments/me`
 * with `asDri: true` — so the fix is one click, not a permission request.
 */
export const PRIORITY_CANDIDATE_MUST_CLAIM = "PRIORITY_CANDIDATE_MUST_CLAIM";

/** Backend error code: tried to change an assignment row belonging to someone else. `details`: their name. */
export const PRIORITY_ASSIGNMENT_NOT_OWNED = "PRIORITY_ASSIGNMENT_NOT_OWNED";

/**
 * Backend error code: the caller holds no assignment row on the priority. Raised by reorder, which is
 * assignee-level — you may rearrange your own Queue whoever the DRI is, but not somebody else's list.
 * `details` carries the priority code.
 */
export const PRIORITY_ASSIGNMENT_NOT_FOUND = "PRIORITY_ASSIGNMENT_NOT_FOUND";

/**
 * The rule fires on any release of your own Focus, so the copy has to name the action the user
 * actually took — "selecting a new Focus task" is wrong when they were closing or parking it.
 *
 * Both point at chat, since that is where people actually write updates; the app has no composer.
 * The swap wording is the ticket's, kept verbatim apart from that pointer.
 */
export const FOCUS_UPDATE_REQUIRED_MESSAGE = {
    /** Picking a different priority to focus on. */
    swap: (task = "your current Focus task") =>
        `Please update ${task} before selecting a new Focus task. You can post the update in chat.`,
    /** Moving the Focus itself to done, on-hold, waiting, blocked or queue — or unassigning yourself. */
    release: (task = "your Focus task") => `Please update ${task} in chat before closing or moving it.`,
} as const;

/** Generic guidance shown when the over-limit assignee is the current user (or unknown). */
export const ASSIGNMENT_LIMIT_MESSAGE =
    "To stay focused, you should only have 1 task in Focus and no more than 2 tasks in Queue.\n" +
    "Focus is the task you commit to completing this week.\n" +
    "If your Focus task is blocked, waiting, or on-hold, you can pick another task from Queue.";

const LIMIT_HINT =
    "Each person can have only 1 task in Focus and at most 2 in Queue. " +
    "Change their status (blocked, waiting, on-hold, or done) or assign a different person.";

/**
 * Returns a friendly message when the error is a Focus/Queue limit violation, else undefined.
 *
 * When the backend names the over-limit assignee (`details`), the message identifies that person so
 * we don't wrongly blame the current user ("you") for another assignee being at their limit.
 */
export function getAssignmentLimitMessage(error: unknown): string | undefined {
    if (!(error instanceof ApiError)) return undefined;
    const body = error.body as { message?: string; details?: string };
    const code = body?.message;
    if (code !== FOCUS_LIMIT_EXCEEDED && code !== QUEUE_LIMIT_EXCEEDED) return undefined;

    const name = body?.details?.trim();
    if (!name) return ASSIGNMENT_LIMIT_MESSAGE;

    const subject =
        code === FOCUS_LIMIT_EXCEEDED
            ? `${name} already has a task in Focus.`
            : `${name} already has 2 tasks in Queue.`;
    return `${subject}\n${LIMIT_HINT}`;
}

/** Best human-readable line the backend gave us, falling back to the status code. */
export function getApiErrorMessage(error: unknown): string | undefined {
    if (!(error instanceof ApiError)) return undefined;
    const body = error.body as { message?: string; details?: string };
    const details = typeof body?.details === "string" ? body.details.trim() : "";
    const message = typeof body?.message === "string" ? body.message.trim() : "";
    return details || message || `Request failed (${error.status})`;
}

/**
 * Returns the Focus-update guidance when the backend rejected the change because the Focus being
 * let go has no status update this week, else undefined.
 *
 * `context` picks the wording: `swap` when the user was choosing a new Focus, `release` when they
 * were changing the Focus task's own status or dropping their assignment.
 */
export function getFocusUpdateRequiredMessage(
    error: unknown,
    context: keyof typeof FOCUS_UPDATE_REQUIRED_MESSAGE = "swap",
): string | undefined {
    if (!(error instanceof ApiError)) return undefined;
    const body = error.body as { message?: string; details?: string };
    if (body?.message !== FOCUS_UPDATE_REQUIRED) return undefined;

    // `details` is the code of the Focus owing the update — which, on a swap, is a *different* priority
    // from the one being promoted. Naming it is why the backend sends it at all: without it a user
    // holding several tasks is told to "update your Focus task" and cannot tell which one.
    const code = body?.details?.trim();
    return FOCUS_UPDATE_REQUIRED_MESSAGE[context](code || undefined);
}

/**
 * Returns guidance when the backend refused a task-level change because the current user is not the
 * DRI, else undefined.
 *
 * `details` carries the DRI's name, so the message can say who to ask. It also points at the one thing
 * the user *can* still do — their own status — because "not allowed" with no alternative reads as a bug.
 */
export function getDriOnlyMessage(error: unknown): string | undefined {
    if (!(error instanceof ApiError)) return undefined;
    const body = error.body as { message?: string; details?: string };
    if (body?.message !== PRIORITY_DRI_ONLY) return undefined;

    const name = body?.details?.trim();
    const who = name ? `${name}, the DRI of this task,` : "Only the DRI of this task";
    return (
        `${who} can change its details, status, assignees or DRI.\n` +
        "You can still change your own status on it from the OneThing page."
    );
}

/**
 * Returns guidance when the backend refused a change to a candidate because the caller is only an
 * assignee, else undefined. Points at the claim button that sits next to the Candidate badge.
 */
export function getCandidateMustClaimMessage(error: unknown): string | undefined {
    if (!(error instanceof ApiError)) return undefined;
    const body = error.body as { message?: string };
    if (body?.message !== PRIORITY_CANDIDATE_MUST_CLAIM) return undefined;

    return (
        "This deliverable has no DRI yet, so nobody owns its definition.\n" +
        "Claim it as DRI first — the Claim as DRI button sits next to the Candidate badge on its detail view."
    );
}

/**
 * Returns guidance when the backend refused a change to somebody else's assignment row, else undefined.
 *
 * Not reachable from the current screens — the own-row endpoints have no target user — but mapped so a
 * future screen that does name a target fails with an explanation rather than a raw code.
 */
export function getAssignmentNotOwnedMessage(error: unknown): string | undefined {
    if (!(error instanceof ApiError)) return undefined;
    const body = error.body as { message?: string; details?: string };
    if (body?.message !== PRIORITY_ASSIGNMENT_NOT_OWNED) return undefined;

    const name = body?.details?.trim();
    const subject = name ? `${name}'s` : "Another assignee's";
    return `${subject} status, IC task and blocker are theirs to set.\nAsk the DRI if the row needs changing on their behalf.`;
}

/**
 * Returns guidance when the caller holds no assignment row on the priority, else undefined.
 *
 * Reorder is assignee-level: your own Queue order is yours to set whoever the DRI is, but a task you
 * are not on is not in your Queue to begin with — so this is a stale list far more often than a real
 * permission problem, and the copy says so.
 */
export function getNotAssignedMessage(error: unknown): string | undefined {
    if (!(error instanceof ApiError)) return undefined;
    const body = error.body as { message?: string; details?: string };
    if (body?.message !== PRIORITY_ASSIGNMENT_NOT_FOUND) return undefined;

    const code = body?.details?.trim();
    const subject = code ? `${code} is not assigned to you` : "One of those tasks is not assigned to you";
    return `${subject}, so it is not yours to reorder.\nRefresh the page — your queue may be out of date.`;
}

/**
 * The authorization codes in one call, for `onError` chains.
 *
 * Ordered most-specific-first: each names a different missing thing — the DRI position, a DRI at all,
 * ownership of the row, an assignment row — and all of them are more useful than the limit copy, which
 * only restates the rules.
 *
 * The one accountability rule left, `FOCUS_UPDATE_REQUIRED`, is deliberately not in this chain: its
 * copy depends on whether the user was swapping or releasing a Focus, so callers pick that context
 * themselves via {@link getFocusUpdateRequiredMessage} before falling through to here.
 */
export function getPriorityAuthzMessage(error: unknown): string | undefined {
    return (
        getDriOnlyMessage(error) ??
        getCandidateMustClaimMessage(error) ??
        getAssignmentNotOwnedMessage(error) ??
        getNotAssignedMessage(error)
    );
}

/**
 * Returns guidance when the save asked for two contradictory things — closing the priority while
 * putting someone into Focus or Queue on it — else undefined.
 *
 * `details` carries that person's name, so the message can say who needs changing; without it the user
 * is told "someone" is still active on a task with several assignees and has to guess.
 */
export function getActiveAssignmentConflictMessage(error: unknown): string | undefined {
    if (!(error instanceof ApiError)) return undefined;
    const body = error.body as { message?: string; details?: string };
    if (body?.message !== ACTIVE_ASSIGNMENT_ON_INACTIVE_PRIORITY) return undefined;

    const name = body?.details?.trim();
    const subject = name ? `${name} is` : "Someone is";
    return (
        `${subject} still set to Focus or Queue on this task, so it cannot be closed or put on hold yet.\n` +
        "Set them to done, waiting or on-hold first — or leave the priority status as it is."
    );
}
